import { JsonDetails } from "@superadmin/components/JsonDetails";
import { PageShell } from "@superadmin/components/PageShell";
import { orpc } from "@superadmin/utils/orpc.tanstack.client";
import { useQuery } from "@tanstack/react-query";
import { LoadingSpinner } from "@voicely/ui-mui/components/LoadingSpinner";
import { Typography } from "@voicely/ui-mui/data-display/Typography";
import { Alert } from "@voicely/ui-mui/feedback/Alert";
import { Button } from "@voicely/ui-mui/form/Button";
import { Card } from "@voicely/ui-mui/layout/Card";
import { Stack } from "@voicely/ui-mui/layout/Stack";

/**
 * Four cross-tenant invariants whose only correct answer is zero. The page to
 * open after any change to who-answers-what.
 */
export const TenancyAuditPage = () => {
	const auditQuery = useQuery(orpc.superAdmin.tenancyAudit.queryOptions());

	if (auditQuery.isPending) {
		return (
			<PageShell title="Tenancy audit">
				<LoadingSpinner text="Checking every workspace against every other..." />
			</PageShell>
		);
	}

	if (auditQuery.error) {
		return (
			<PageShell title="Tenancy audit">
				<Alert severity="error">{auditQuery.error.message}</Alert>
			</PageShell>
		);
	}

	const audit = auditQuery.data;

	return (
		<PageShell
			title="Tenancy audit"
			subtitle="Four invariants whose only correct answer is zero"
			actions={
				<Button variant="outlined" size="small" onClick={() => auditQuery.refetch()}>
					Re-run
				</Button>
			}
		>
			<Stack spacing={2}>
				<Alert severity={audit.ok ? "success" : "error"}>
					{audit.ok
						? "All four invariants hold."
						: "At least one invariant is violated — every finding below is a row that must not exist."}
				</Alert>

				{audit.findings.map((finding) => (
					<Card
						key={finding.check}
						variant="outlined"
						sx={{
							p: 2,
							borderColor: finding.count > 0 ? "error.main" : "surface.border",
						}}
					>
						<Stack spacing={1}>
							<Stack direction="row" spacing={1.5} alignItems="baseline">
								<Typography
									variant="h4"
									sx={{ color: finding.count > 0 ? "error.main" : "success.main" }}
								>
									{finding.count}
								</Typography>
								<Typography variant="subtitle2">{finding.check}</Typography>
							</Stack>
							<Typography variant="body2" color="text.secondary">
								{finding.meaning}
							</Typography>
							{finding.sample.length > 0 && (
								<JsonDetails
									label={`Sample rows (${finding.sample.length})`}
									value={finding.sample}
								/>
							)}
						</Stack>
					</Card>
				))}
			</Stack>
		</PageShell>
	);
};
